// Port of new arty's components/arty/community/AskForms.tsx (arty repo).
// Client divergences: copy is `arty-card` `community-ask.*`, the textarea is
// @artstorefronts/ui's, and each form's payload (and so whether it can submit) comes from
// `~/lib/community-ask`, where it is spec'd, instead of being built inline.
import { useId, useState } from 'react';
import { Plus, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Textarea } from '@artstorefronts/ui';

import {
  AskFormShell,
  Chip,
  DurationChips,
  Field,
  FieldSet,
  FIELD_CLASS,
} from '~/components/arty/community/ask-fields';
import {
  CRITIQUE_FOCUS_IDS,
  DESTINATION,
  EXPERIENCE,
  INTENT_MIN_CHARS,
  POLL_LABEL_MAX,
  POLL_OPTION_MAX,
  POLL_OPTION_MIN,
  WINDOW_DAYS,
  critiqueDraft,
  pollDraft,
  pricingDraft,
  ratingDraft,
  type AskDraft,
} from '~/lib/community-ask';

/** What the card knows about the piece, handed to the form it opens. */
export type InitialArtwork = {
  title?: string;
  medium?: string;
  dimensions?: string;
  thumb?: string | null;
};

export type AskFormProps = {
  error: string | null;
  initialArtwork?: InitialArtwork;
  onSubmit: (draft: AskDraft) => void;
  submitting: boolean;
};

/** Submit only ever sends a draft the lib said was complete. */
function sendIf(draft: AskDraft | null, onSubmit: (draft: AskDraft) => void) {
  return () => {
    if (!draft) {
      return;
    }
    onSubmit(draft);
  };
}

export function RatingAskForm({ error, onSubmit, submitting }: AskFormProps) {
  const [durationDays, setDurationDays] = useState(7);
  return (
    <AskFormShell
      canSubmit
      error={error}
      kind="rating"
      onSubmit={sendIf(ratingDraft(durationDays), onSubmit)}
      submitting={submitting}
    >
      <DurationChips choices={WINDOW_DAYS.rating} onChange={setDurationDays} value={durationDays} />
    </AskFormShell>
  );
}

export function PricingAskForm({ error, onSubmit, submitting }: AskFormProps) {
  const { t } = useTranslation('arty-card');
  const ids = useId();
  const [low, setLow] = useState('');
  const [high, setHigh] = useState('');
  const [durationDays, setDurationDays] = useState(7);
  const draft = pricingDraft(low, high, durationDays);

  return (
    <AskFormShell
      canSubmit={draft !== null}
      error={error}
      kind="pricing"
      onSubmit={sendIf(draft, onSubmit)}
      submitting={submitting}
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field htmlFor={`${ids}-low`} label={t('community-ask.pricing.low')} required>
          <input
            className={FIELD_CLASS}
            id={`${ids}-low`}
            inputMode="decimal"
            onChange={(event) => setLow(event.target.value)}
            placeholder="$50"
            value={low}
          />
        </Field>
        <Field htmlFor={`${ids}-high`} label={t('community-ask.pricing.high')} required>
          <input
            className={FIELD_CLASS}
            id={`${ids}-high`}
            inputMode="decimal"
            onChange={(event) => setHigh(event.target.value)}
            placeholder="$2,000"
            value={high}
          />
        </Field>
      </div>
      <p className="text-xs text-muted-foreground">{t('community-ask.pricing.hint')}</p>
      <DurationChips choices={WINDOW_DAYS.pricing} onChange={setDurationDays} value={durationDays} />
    </AskFormShell>
  );
}

function Select({
  id,
  none,
  onChange,
  options,
  prefix,
  value,
}: {
  id: string;
  none: string;
  onChange: (value: string) => void;
  options: Record<string, string>;
  prefix: string;
  value: string;
}) {
  const { t } = useTranslation('arty-card');
  return (
    <select className={FIELD_CLASS} id={id} onChange={(event) => onChange(event.target.value)} value={value}>
      <option value="">{none}</option>
      {Object.keys(options).map((key) => (
        <option key={key} value={key}>
          {t(`${prefix}.${key}`)}
        </option>
      ))}
    </select>
  );
}

function IntentCount({ length }: { length: number }) {
  const { t } = useTranslation('arty-card');
  if (length < INTENT_MIN_CHARS) {
    return (
      <p className="text-xs text-muted-foreground">
        {t('community-ask.critique.intent-short', { length, min: INTENT_MIN_CHARS })}
      </p>
    );
  }
  return <p className="text-xs text-muted-foreground">{t('community-ask.critique.intent-count', { length })}</p>;
}

export function CritiqueAskForm({ error, initialArtwork, onSubmit, submitting }: AskFormProps) {
  const { t } = useTranslation('arty-card');
  const ids = useId();
  const [fields, setFields] = useState({
    title: initialArtwork?.title ?? '',
    medium: initialArtwork?.medium ?? '',
    dimensions: initialArtwork?.dimensions ?? '',
    stage: 'FINISHED' as 'WIP' | 'FINISHED',
    intent: '',
    focusAreas: [] as string[],
    experience: '',
    destination: '',
    canPaintOver: true,
    windowDays: WINDOW_DAYS.critique[0],
  });
  const draft = critiqueDraft(fields);

  const set = (patch: Partial<typeof fields>) => {
    setFields((current) => ({ ...current, ...patch }));
  };

  const toggleFocus = (id: string) => {
    const focusAreas = fields.focusAreas.includes(id)
      ? fields.focusAreas.filter((focus) => focus !== id)
      : [...fields.focusAreas, id];
    set({ focusAreas });
  };

  const text = (key: 'title' | 'medium' | 'dimensions' | 'intent') => {
    return { id: `${ids}-${key}`, value: fields[key], onChange: (event: any) => set({ [key]: event.target.value }) };
  };

  return (
    <AskFormShell
      canSubmit={draft !== null}
      error={error}
      kind="critique"
      onSubmit={sendIf(draft, onSubmit)}
      submitting={submitting}
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Field htmlFor={`${ids}-title`} label={t('community-ask.critique.title')} required>
          <input
            className={FIELD_CLASS}
            placeholder={t('community-ask.critique.title-placeholder')}
            {...text('title')}
          />
        </Field>
        <Field htmlFor={`${ids}-medium`} label={t('community-ask.critique.medium')} required>
          <input
            className={FIELD_CLASS}
            placeholder={t('community-ask.critique.medium-placeholder')}
            {...text('medium')}
          />
        </Field>
        <Field htmlFor={`${ids}-dimensions`} label={t('community-ask.critique.size')}>
          <input className={FIELD_CLASS} placeholder={'24 x 36"'} {...text('dimensions')} />
        </Field>
      </div>

      <FieldSet legend={t('community-ask.critique.stage')}>
        <div className="flex flex-wrap gap-2">
          <Chip onClick={() => set({ stage: 'FINISHED' })} pressed={fields.stage === 'FINISHED'}>
            {t('community-ask.critique.finished')}
          </Chip>
          <Chip onClick={() => set({ stage: 'WIP' })} pressed={fields.stage === 'WIP'}>
            {t('community-ask.critique.wip')}
          </Chip>
        </div>
      </FieldSet>

      <Field
        hint={t('community-ask.critique.intent-hint')}
        htmlFor={`${ids}-intent`}
        label={t('community-ask.critique.intent')}
        required
      >
        <Textarea
          className="min-h-28 rounded-xl border-border bg-background"
          placeholder={t('community-ask.critique.intent-placeholder')}
          {...text('intent')}
        />
        <IntentCount length={fields.intent.trim().length} />
      </Field>

      <FieldSet hint={t('community-ask.critique.focus-hint')} legend={t('community-ask.critique.focus')} required>
        <div className="flex flex-wrap gap-2">
          {CRITIQUE_FOCUS_IDS.map((id) => (
            <Chip key={id} onClick={() => toggleFocus(id)} pressed={fields.focusAreas.includes(id)}>
              {t(`community-ask.focus.${id}`)}
            </Chip>
          ))}
        </div>
      </FieldSet>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field htmlFor={`${ids}-experience`} label={t('community-ask.critique.experience')}>
          <Select
            id={`${ids}-experience`}
            none={t('community-ask.critique.experience-none')}
            onChange={(experience) => set({ experience })}
            options={EXPERIENCE}
            prefix="community-ask.experience"
            value={fields.experience}
          />
        </Field>
        <Field htmlFor={`${ids}-destination`} label={t('community-ask.critique.destination')}>
          <Select
            id={`${ids}-destination`}
            none={t('community-ask.critique.destination-none')}
            onChange={(destination) => set({ destination })}
            options={DESTINATION}
            prefix="community-ask.destination"
            value={fields.destination}
          />
        </Field>
      </div>

      <DurationChips
        choices={WINDOW_DAYS.critique}
        onChange={(windowDays) => set({ windowDays })}
        value={fields.windowDays}
      />

      <label className="flex min-h-11 items-center gap-2 text-sm text-foreground">
        <input
          checked={fields.canPaintOver}
          className="size-4 accent-green-600"
          onChange={(event) => set({ canPaintOver: event.target.checked })}
          type="checkbox"
        />
        {t('community-ask.critique.paint-over')}
      </label>
    </AskFormShell>
  );
}

function PollOption({
  index,
  onChange,
  onRemove,
  value,
}: {
  index: number;
  onChange: (value: string) => void;
  onRemove?: () => void;
  value: string;
}) {
  const { t } = useTranslation('arty-card');
  const label = t('community-ask.poll.option', { index: index + 1 });
  return (
    <div className="flex items-center gap-2">
      <input
        aria-label={label}
        className={FIELD_CLASS}
        maxLength={POLL_LABEL_MAX}
        onChange={(event) => onChange(event.target.value)}
        placeholder={label}
        value={value}
      />
      {onRemove ? (
        <button
          aria-label={t('community-ask.poll.remove', { index: index + 1 })}
          className="flex size-11 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:bg-muted"
          onClick={onRemove}
          type="button"
        >
          <X className="size-4" />
        </button>
      ) : null}
    </div>
  );
}

export function PollAskForm({ error, initialArtwork, onSubmit, submitting }: AskFormProps) {
  const { t } = useTranslation('arty-card');
  const ids = useId();
  const imageTitle = initialArtwork?.title;
  const [question, setQuestion] = useState(() => {
    if (!imageTitle) {
      return '';
    }
    return t('community-ask.poll.question-default', { title: imageTitle });
  });
  const [options, setOptions] = useState<string[]>(['', '']);
  const [durationDays, setDurationDays] = useState(7);
  const draft = pollDraft(question, options, durationDays);
  const removable = options.length > POLL_OPTION_MIN;

  const setOption = (index: number, value: string) => {
    setOptions((current) => current.map((option, at) => (at === index ? value : option)));
  };

  const removeOption = (index: number) => {
    setOptions((current) => current.filter((_, at) => at !== index));
  };

  return (
    <AskFormShell
      canSubmit={draft !== null}
      error={error}
      kind="poll"
      onSubmit={sendIf(draft, onSubmit)}
      submitting={submitting}
    >
      <Field htmlFor={`${ids}-question`} label={t('community-ask.poll.question')} required>
        <input
          className={FIELD_CLASS}
          id={`${ids}-question`}
          onChange={(event) => setQuestion(event.target.value)}
          placeholder={t('community-ask.poll.question-placeholder')}
          value={question}
        />
      </Field>

      <FieldSet hint={t('community-ask.poll.options-hint')} legend={t('community-ask.poll.options')} required>
        <div className="flex flex-col gap-2">
          {options.map((option, index) => (
            <PollOption
              index={index}
              key={index}
              onChange={(value) => setOption(index, value)}
              onRemove={removable ? () => removeOption(index) : undefined}
              value={option}
            />
          ))}
        </div>
        {options.length < POLL_OPTION_MAX ? (
          <button
            className="inline-flex min-h-11 w-fit items-center gap-1.5 text-sm font-medium text-foreground"
            onClick={() => setOptions((current) => [...current, ''])}
            type="button"
          >
            <Plus className="size-4" />
            {t('community-ask.poll.add')}
          </button>
        ) : null}
      </FieldSet>

      <DurationChips choices={WINDOW_DAYS.poll} onChange={setDurationDays} value={durationDays} />
    </AskFormShell>
  );
}
